<template lang="pug">
AppWizardStep(is-last-step)
  //- Remove the footer, the wizard will handle it.
  .hidden(slot="footer")
  transition-group.job-site-rules-wrapper(
    name="rules-fade-list"
    tag="div"
  )
    //- RULE(s)
    //- FIXME: Removing rule weird breaks style while removing it
    NewJobSiteRule(
      v-for="(rule, index) in rules"
      ref="rules"
      :key="rule.number"
      :rule="rule"
      :removable="index > 0"
      :teams-enabled="teamsEnabled"
      :used-member-ids="usedUserIds"
      :expanded-by-default="!editing"
      :teams-for-select="teamsForSelect"
      :members-for-select="membersForSelect"
      @remove="onRemoveRule(index)"
      @update="onUpdateRule(index, $event)"
    )
  .d-flex.align-items-center.justify-content-center.flex-row(v-if="canAddNewGroup")
    AppButton(
      type="outline"
      size="sm"
      modified="text-center"
      @click="onAddRule"
    )
      | Add new rule
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { JOB_SITE_ACTIONS } from '~/app_constants';
import NewJobSiteRule from './NewJobSiteRule.vue';

const getDefaultRule = () => ({
  id: null,
  number: 1,
  team_ids: [],
  user_ids: [],
  start_task_id: null,
  start_project_id: null,
  exit_action: JOB_SITE_ACTIONS.doNothing,
  enter_action: JOB_SITE_ACTIONS.doNothing,
});

export default {
  name: 'NewJobSiteWizardRulesStep',
  components: { NewJobSiteRule },
  props: {
    value: {
      type: Object,
      required: true,
    },
    editing: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      rules: [],
      originalTeams: null,
      originalUsers: null,
    };
  },
  computed: {
    ...mapState({ teamsEnabled: 'teams_enabled' }),
    ...mapGetters(['allTeams', 'allTeamsMap', 'allMembers']),
    // Track the user ids for each group so that we don't
    // show them in the subsequent rules.
    usedUserIds() {
      return new Set(
        this.rules.flatMap((rule) => [
          ...rule.user_ids,
          // Don't compute any teams' members if we have the addon disabled
          // #teams-addon
          ...(this.teamsEnabled ? rule.team_ids : []).flatMap((teamId) => this.allTeamsMap.get(teamId).user_ids),
        ]),
      );
    },
    // Track the team ids for each group so that we don't
    // show them in the subsequent rules.
    usedTeamIds() {
      return new Set(this.rules.flatMap((rule) => rule.team_ids || []));
    },
    teamsForSelect() {
      // Avoid unecessary work if teams is not enabled
      // #teams-addon
      if (!this.teamsEnabled) {
        return [];
      }

      return this.allTeams.reduce((teams, team) => {
        if (!this.usedTeamIds.has(team.id)) {
          teams.push({ id: team.id, text: team.name });
        }

        return teams;
      }, []);
    },
    membersForSelect() {
      // Loop through the members list and gather those that haven't been used
      return this.allMembers.reduce((members, member) => {
        if (!this.usedUserIds.has(member.id)) {
          members.push({ id: member.id, text: member.name });
        }

        return members;
      }, []);
    },
    canAddNewGroup() {
      // If there are unused user ids, we can add a new group.
      return this.usedUserIds.size < this.allMembers.length;
    },
  },
  mounted() {
    this.originalTeams = new Map(
      // Don't compute teams if the teams addon is disabled.
      // #teams-addon
      (this.teamsEnabled ? this.value.teams : []).map((team) => [team.team_id, { ...team }]),
    );
    this.originalUsers = new Map(this.value.users.map((user) => [user.user_id, { ...user }]));

    // If we're editing, we want to initialize the
    // rules with the job site's rules.
    if (this.editing) {
      // Rather than using a bunch of methods to get the rules and
      // create essentially what will be a "transient" shape.
      const { users, teams, groups } = this.value;

      for (const group of groups) {
        this.rules.push({
          ...getDefaultRule(),
          ...group,
          user_ids: users.filter((user) => user.job_site_rule_id === group.id).map((user) => user.user_id),
          // Don't compute any teams related things if we don't have the teams addon.
          // #teams-addon
          team_ids: this.teamsEnabled
            ? teams.filter((team) => team.job_site_rule_id === group.id).map((team) => team.team_id)
            : [],
        });
      }
    } else {
      // If we're creating, we want to initialize the rules
      // with the default rule.
      this.rules.push(getDefaultRule());
    }
  },
  methods: {
    onRemoveRule(index) {
      // Remove the `rule` with matching `number`
      this.rules.splice(index, 1);
      this.syncRulesWithJobSite();
    },
    onAddRule() {
      const { rules } = this;
      const lastRule = rules[rules.length - 1];

      this.rules.push({
        ...getDefaultRule(),
        number: lastRule.number + 1,
      });
    },
    onUpdateRule(ruleIndex, rule) {
      // Update the actual rule
      this.rules.splice(ruleIndex, 1, rule);
      this.syncRulesWithJobSite();
    },
    syncRulesWithJobSite() {
      // Now that we've updated the actual rules, we can easily loop
      // through the `user_ids` and update the `groups`, `teams` &
      // `users` on the job site, based on the values of the rule.
      const teams = [];
      const users = [];
      const groups = [];

      for (const { user_ids: userIds = [], team_ids: teamIds = [], ...restRule } of this.rules) {
        groups.push(restRule);
        users.push(
          ...userIds.map((userId) => {
            const originalUser = this.originalUsers.get(userId);
            // We only have to include the `job_site.users[INDEX].id` if we originally had
            // a matching original user and the its rule is the same.
            const shouldIncludeUserId = originalUser ? originalUser.job_site_rule_id === restRule.id : false;

            return {
              user_id: userId,
              group_number: restRule.number,
              ...(shouldIncludeUserId ? { id: originalUser.id } : {}),
            };
          }),
        );
        teams.push(
          ...teamIds.map((teamId) => {
            const originalTeam = this.originalTeams.get(teamId);
            // We only have to include the `job_site.teams[INDEX].id` if we originally had
            // a matching original team and its rule is the same.
            const shouldIncludeTeamId = originalTeam ? originalTeam.job_site_rule_id === restRule.id : false;

            return {
              team_id: teamId,
              group_number: restRule.number,
              ...(shouldIncludeTeamId ? { id: originalTeam.id } : {}),
            };
          }),
        );
      }

      // Update the parent's `v-model`
      this.$emit('input', { ...this.value, teams, users, groups });
    },
  },
};
</script>

<style scoped>
.job-site-rules-wrapper {
  position: relative;
}

.rules-fade-list-move,
.rules-fade-list-enter-active,
.rules-fade-list-leave-active {
  transition: all 0.4s;
}

.rules-fade-list-enter,
.rules-fade-list-leave-to {
  opacity: 0;
  transform: translateX(30px);
}

.rules-fade-list-leave-active {
  position: absolute;
  width: 100%;
}
</style>
